import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { PermissionConfigService, MenuItemPermission, ActionPermission } from './permission-config.service';

export interface UserPermissions {
  permissions: string[];
  role: {
    identification: string;
  name: string;
    level: string;
  };
  subscriberId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private userPermissionsSubject = new BehaviorSubject<UserPermissions | null>(null);
  public userPermissions$ = this.userPermissionsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private permissionConfig: PermissionConfigService
  ) {}

  /**
   * Set user permissions (called after login)
   */
  setUserPermissions(permissions: UserPermissions): void {
    this.userPermissionsSubject.next(permissions);
  }

  /**
   * Get current user permissions
   */
  getUserPermissions(): UserPermissions | null {
    return this.userPermissionsSubject.value;
  }

  /**
   * Check if user has a specific permission
   */
  hasPermission(permission: string): boolean {
    const userPermissions = this.getUserPermissions();
    if (!userPermissions || !userPermissions.permissions) {
      return false;
    }
    
    // SUPER_DEV_ADMIN bypasses all permission checks
    if (userPermissions.role?.identification === 'SUPER_DEV_ADMIN') {
      return true;
    }
    
    return userPermissions.permissions.includes(permission);
  }

  /**
   * Check if user has any of the specified permissions
   */
  hasAnyPermission(permissions: string[]): boolean {
    const userPermissions = this.getUserPermissions();
    if (!userPermissions || !userPermissions.permissions) {
      return false;
    }
    
    // SUPER_DEV_ADMIN bypasses all permission checks
    if (userPermissions.role?.identification === 'SUPER_DEV_ADMIN') {
      return true;
    }
    
    return permissions.some(permission => userPermissions.permissions.includes(permission));
  }

  /**
   * Check if user has all of the specified permissions
   */
  hasAllPermissions(permissions: string[]): boolean {
    const userPermissions = this.getUserPermissions();
    if (!userPermissions || !userPermissions.permissions) {
      return false;
    }
    
    // SUPER_DEV_ADMIN bypasses all permission checks
    if (userPermissions.role?.identification === 'SUPER_DEV_ADMIN') {
      return true;
    }
    
    return permissions.every(permission => userPermissions.permissions.includes(permission));
  }

  /**
   * Check if user has a specific role
   */
  hasRole(roleIdentification: string): boolean {
    const userPermissions = this.getUserPermissions();
    if (!userPermissions || !userPermissions.role) {
      return false;
    }
    return userPermissions.role.identification === roleIdentification;
  }

  /**
   * Check if user has any of the specified roles
   */
  hasAnyRole(roles: string[]): boolean {
    const userPermissions = this.getUserPermissions();
    if (!userPermissions || !userPermissions.role) {
      return false;
    }
    return roles.includes(userPermissions.role.identification);
  }

  /**
   * Check if user is global admin (CSM, SM, SUPER_DEV_ADMIN)
   */
  isGlobalAdmin(): boolean {
    return this.hasAnyRole(['CSM', 'SM', 'SUPER_DEV_ADMIN']);
  }

  /**
   * Check if user is subscriber admin (SA, A)
   */
  isSubscriberAdmin(): boolean {
    return this.hasAnyRole(['SA', 'A']);
  }

  /**
   * Check if user is FSR
   */
  isFSR(): boolean {
    return this.hasRole('FSR');
  }

  /**
   * Get user role level
   */
  getUserRoleLevel(): string | null {
    const userPermissions = this.getUserPermissions();
    return userPermissions?.role?.level || null;
  }

  /**
   * Get user role identification
   */
  getUserRole(): string | null {
    const userPermissions = this.getUserPermissions();
    return userPermissions?.role?.identification || null;
  }

  /**
   * Check if user can access subscriber data
   */
  canAccessSubscriber(subscriberId: string): boolean {
    const userPermissions = this.getUserPermissions();
    
    // Global admins can access any subscriber
    if (this.isGlobalAdmin()) {
      return true;
    }

    // Subscriber-level users can only access their own subscriber
    return userPermissions?.subscriberId === subscriberId;
  }

  /**
   * Clear permissions (called on logout)
   */
  clearPermissions(): void {
    this.userPermissionsSubject.next(null);
  }

  /**
   * Refresh permissions from server
   */
  refreshPermissions(): Observable<UserPermissions> {
    return this.http.get<UserPermissions>(`${environment.apiUrl}/users/me/permissions`);
  }

  /**
   * Get all available permissions from server
   */
  getAllPermissions(): Observable<any[]> {
    return this.http.get<{message: string, permissions: any[]}>(`${environment.apiUrl}/permissions`)
      .pipe(
        map(response => response.permissions || [])
      );
  }

  /**
   * Get permissions by category
   */
  getPermissionsByCategory(): Observable<{ [category: string]: any[] }> {
    return this.http.get<{ [category: string]: any[] }>(`${environment.apiUrl}/permissions/categories`);
  }

  /**
   * Check if user can see a menu item
   */
  canSeeMenuItem(routerLink: string): boolean {
    const menuConfig = this.permissionConfig.getMenuPermissions(routerLink);
    if (!menuConfig) {
      // If no configuration found, allow by default
      return true;
    }

    return this.checkConfiguredPermissions(menuConfig);
  }

  /**
   * Check if user can perform an action
   */
  canPerformAction(action: string): boolean {
    const actionConfig = this.permissionConfig.getActionPermissions(action);
    if (!actionConfig) {
      // If no configuration found, allow by default
      return true;
    }

    return this.checkConfiguredPermissions(actionConfig);
  }

  /**
   * Filter menu items based on user permissions
   */
  filterMenuItems(menuItems: any[]): any[] {
    const filteredItems: any[] = [];
    
    for (let i = 0; i < menuItems.length; i++) {
      const item = menuItems[i];
      
      // Handle separators - we'll decide whether to include them based on context
      if (item.separator) {
        continue; // Skip separators for now, we'll add them back conditionally
      }
      
      // Check if item has visible property set to false
      if (item.visible === false) {
        continue;
      }
      
      // Check main item permission
      if (item.routerLink && !this.canSeeMenuItem(item.routerLink)) {
        continue;
      }

      // Check sub-items if they exist
      if (item.items && item.items.length > 0) {
        const originalItems = [...item.items];
        item.items = this.filterMenuItems(item.items);
        
        // Only keep the parent section if it has visible children
        if (item.items.length > 0) {
          // Add separator before this section if there are already items in filteredItems
          if (filteredItems.length > 0) {
            filteredItems.push({ separator: true });
          }
          filteredItems.push(item);
        }
        // If no visible children, skip this entire section (including its label)
      } else {
        // Single menu item without children
        // Add separator before this item if there are already items in filteredItems
        if (filteredItems.length > 0) {
          filteredItems.push({ separator: true });
        }
        filteredItems.push(item);
      }
    }
    
    return filteredItems;
  }

  /**
   * Check configured permissions and roles
   */
  private checkConfiguredPermissions(config: MenuItemPermission | ActionPermission): boolean {
    const userPermissions = this.getUserPermissions();
    
    // SUPER_DEV_ADMIN bypasses all permission and role checks
    if (userPermissions?.role?.identification === 'SUPER_DEV_ADMIN') {
      return true;
    }

    let hasPermission = true;
    let hasRole = true;

    // Check permissions
    if (config.permissions && config.permissions.length > 0) {
      if (config.requireAll) {
        hasPermission = this.hasAllPermissions(config.permissions);
      } else {
        hasPermission = this.hasAnyPermission(config.permissions);
      }
    }

    // Check roles
    if (config.roles && config.roles.length > 0) {
      hasRole = this.hasAnyRole(config.roles);
    }

    // Both permission and role checks must pass
    return hasPermission && hasRole;
  }

  /**
   * Get filtered navigation items for current user
   */
  getFilteredMenuItems(allMenuItems: any[]): any[] {
    return this.filterMenuItems(JSON.parse(JSON.stringify(allMenuItems)));
  }
}